<template>
  <v-row align="center">
      <v-col class="shrink ml-4 ml-md-0" v-if="isSummaryRoute" cols="2" md="auto">
        <fp-btn @click="goBackToForm()" height="40px">
          <v-icon>mdi-arrow-left</v-icon>
        </fp-btn>
      </v-col>
      <v-col class="shrink" cols="9" md="auto">
        <template v-if="store.deal">
          <div class="d-flex">
            <deal-badge
              class="client-autocomplete"
              :deal="store.deal"
              :small="true"/>
            <v-tooltip right>
              <template #activator="{on}">
                <fp-btn v-on="on" text icon @click="store.deleteSimulationFromDeal()"><v-icon>mdi-close-circle-outline</v-icon></fp-btn>
              </template>
              {{ I18NGetter().useCalculatorNavigationComponent.UNLOAD_DEAL }}
            </v-tooltip>
          </div>
        </template>
        <fp-btn v-else :disabled="!store.simulation" @click="openLoadDealModal()">
          <v-icon class="mr-2">mdi-briefcase-upload-outline</v-icon>
          {{ I18NGetter().useCalculatorNavigationComponent.LOAD_DEAL }}
        </fp-btn>
      </v-col>
      <v-spacer></v-spacer>
    <template v-if="isSummaryRoute">
      <v-col class="shrink" v-if="version === CalculatorVersion.V2 && $vuetify.breakpoint.mdAndUp">
        <simulation-toolbar-zoom />
      </v-col>
      <v-col
        class="shrink"
        v-if="$vuetify.breakpoint.mdAndUp && $env.getAppDomainConfig().calculator.hasVersionSwitcher">
        <v-btn-toggle
          :class="$onboarding.tours.MORTGAGE_CALCULATOR.step.VERSION"
          v-model="version"
          @change="onVersionChange"
          mandatory
          dense>
          <v-btn
            height="40px"
            v-for="(label, index) in versionLabels"
            :value="index"
            :key="label">
            {{ label }}
          </v-btn>
        </v-btn-toggle>
      </v-col>
    </template>
    <v-col v-else class="d-flex justify-end">
      <v-tooltip left>
        <template #activator="{on}">
          <fp-btn v-on="on" :click="() => clear()">{{ I18NGetter().useCalculatorNavigationComponent.CLEAR }}</fp-btn>
        </template>
        {{ I18NGetter().useCalculatorNavigationComponent.CLEAR_FORM }}
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
import {computed, getCurrentInstance} from 'vue';
import {useFormNavigation} from '@/components/calculator/form/useSimulationForm';
import {useSimulationStore} from '@/components/calculator/services/SimulationStore';
import {CalculatorVersion, useCalculatorVersion} from '@/components/calculator/useCalculatorVersion';
import {Routes} from '@/router/Routes';
import {ProductType} from '@/commons/enums/ProductType';
import SimulationToolbarZoom from '@/components/calculator/results/toolbar/SimulationToolbarZoom.vue';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import Deal from '@/models/Deal';
import SimulationDealPicker from '@/components/calculator/results/SimulationDealPicker.vue';
import {i18nFormatter} from '@/services/StringFormatter';

const store = useSimulationStore();
const {goBackToForm,} = useFormNavigation();
const {version,} = useCalculatorVersion();
const instance = getCurrentInstance();

const versionLabels = computed<string[]>(() => Object.keys(CalculatorVersion).filter(x => !(parseInt(x) >= 0)));
const newCalculatorSummaryRoutes: string[] = Object.values(ProductType)
  .map(productType => Routes.NEW_CALCULATOR.SUMMARY(productType));
const isSummaryRoute = computed<boolean>(() => newCalculatorSummaryRoutes.includes(instance?.proxy.$route.name ?? ''));

const onVersionChange = (version: CalculatorVersion) => {
  // TODO: Remove this after the new calculator is fully launched, it's responsible for refreshing the data during the transition from v1 to v2
  if (version === CalculatorVersion.V2) {
    store.init(store.productType!, store.simulationId, store.deal as Deal);
  }
};

const openLoadDealModal = async() => {
  const deal = await instance?.proxy.$modalService.open<Deal>(SimulationDealPicker, {
    productType: store.productType,
    simulationId: store.simulationId,
  }) ?? null;
  if (deal) {
    await store.init(store.productType!, deal?.simulationId, deal);
  }
};

const clear = async() => {
  const response = await instance?.proxy.$modalService.openConfirmModal({
    description: store.deal?.name ? i18nFormatter(
      I18NGetter().useCalculatorNavigationComponent.NEW_TRANSLATION_CLEAR_FORM_DESCRIPTION_DEAL,
      {dealName: store.deal?.name,})
      : I18NGetter().useCalculatorNavigationComponent.NEW_TRANSLATION_CLEAR_FORM_DESCRIPTION_NO_DEAL,
    title: I18NGetter().useCalculatorNavigationComponent.CLEAR_FORM_TITLE,
  }, I18NGetter().useCalculatorNavigationComponent.CANCEL, I18NGetter().useCalculatorNavigationComponent.CLEAR);
  if (response) {
    await store.clear();
    await store.createOrUpdateSimulation();
    instance?.proxy.$snackbarService.openSuccessSnackbar(
      I18NGetter().useCalculatorNavigationComponent.CLEAR_SUCCESS
    );
  }
};
</script>
